import { useState, useCallback, useRef } from 'react';

/**
 * A hook for managing confirmation dialogs with optional async actions.
 *
 * @example
 * // Basic usage
 * const { confirm, dialogProps } = useConfirmDialog();
 *
 * // Show confirm dialog and wait for response
 * const confirmed = await confirm({
 *   title: 'Delete Item',
 *   message: 'Are you sure?',
 * });
 * if (confirmed) {
 *   await deleteItem();
 * }
 *
 * @example
 * // With action (handles loading state automatically)
 * const { confirmWithAction, dialogProps } = useConfirmDialog();
 *
 * await confirmWithAction(
 *   { title: 'Delete', message: 'Are you sure?' },
 *   async () => { await deleteItem(); }
 * );
 *
 * // In JSX
 * <ConfirmDialog {...dialogProps} />
 */

export interface ConfirmDialogConfig {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
}

export interface ConfirmDialogState extends ConfirmDialogConfig {
  isOpen: boolean;
  isLoading: boolean;
}

interface PendingConfirm {
  resolve: (confirmed: boolean) => void;
  action?: () => Promise<void>;
}

const defaultState: ConfirmDialogState = {
  isOpen: false,
  isLoading: false,
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  type: 'danger',
};

export function useConfirmDialog() {
  const [state, setState] = useState<ConfirmDialogState>(defaultState);
  const pendingRef = useRef<PendingConfirm | null>(null);

  /**
   * Show confirm dialog and return a promise that resolves to true if confirmed
   */
  const confirm = useCallback(
    (config: ConfirmDialogConfig): Promise<boolean> => {
      return new Promise(resolve => {
        pendingRef.current = { resolve };
        setState({
          isOpen: true,
          isLoading: false,
          title: config.title,
          message: config.message,
          confirmText: config.confirmText || 'Delete',
          cancelText: config.cancelText || 'Cancel',
          type: config.type || 'danger',
        });
      });
    },
    []
  );

  /**
   * Show confirm dialog with an action to execute on confirm
   * Returns true if action completed successfully, false if cancelled or failed
   */
  const confirmWithAction = useCallback(
    (
      config: ConfirmDialogConfig,
      action: () => Promise<void>
    ): Promise<boolean> => {
      return new Promise(resolve => {
        pendingRef.current = { resolve, action };
        setState({
          isOpen: true,
          isLoading: false,
          title: config.title,
          message: config.message,
          confirmText: config.confirmText || 'Delete',
          cancelText: config.cancelText || 'Cancel',
          type: config.type || 'danger',
        });
      });
    },
    []
  );

  /**
   * Handle confirm button click
   */
  const handleConfirm = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) return;

    if (pending.action) {
      // Has action to execute
      setState(prev => ({ ...prev, isLoading: true }));
      try {
        await pending.action();
        pending.resolve(true);
        setState(defaultState);
        pendingRef.current = null;
      } catch (error) {
        // Keep dialog open on error, stop loading
        setState(prev => ({ ...prev, isLoading: false }));
        // Don't resolve - let the caller handle retry
      }
    } else {
      // Simple confirm without action
      pending.resolve(true);
      setState(defaultState);
      pendingRef.current = null;
    }
  }, []);

  /**
   * Handle cancel button click
   */
  const handleCancel = useCallback(() => {
    const pending = pendingRef.current;
    if (pending) {
      pending.resolve(false);
    }
    setState(defaultState);
    pendingRef.current = null;
  }, []);

  /**
   * Props to spread on ConfirmDialog component
   */
  const dialogProps = {
    isOpen: state.isOpen,
    title: state.title,
    message: state.message,
    confirmText: state.confirmText!,
    cancelText: state.cancelText!,
    type: state.type!,
    isLoading: state.isLoading,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };

  return {
    state,
    confirm,
    confirmWithAction,
    handleConfirm,
    handleCancel,
    dialogProps,
  };
}

export type UseConfirmDialogReturn = ReturnType<typeof useConfirmDialog>;
